import FormRow from 'Components/FormRow';
import InputHandler from 'Components/InputHandler';
import OptionChooser from 'Components/OptionChooser';
import BlockWrapper from 'Components/_NewElements/BlockWrapper';
import type { IEQ5D } from 'neuro-schemas';
import { eq5d, Task_Progress } from 'neuro-schemas';
import { isPartialDate, nowPartialDate, partialDateFromDate, partialDateToValue } from 'neuro-utils';
import * as React from 'react';
import { useMyServiceContext } from '../..';
import { omitControlProps } from 'Utility/documentHandling';
import { formatPartialDate } from 'Utility/date';
import browserLanguage from 'Utility/browserLanguage';
import { useAppSelector } from 'Store/index';

export const EQ5D = (): React.JSX.Element => {
  const locale = useAppSelector(s => s.settings.userSettings.uiLanguage) ?? browserLanguage;
  const { editing, setEditingData, viewing, setViewingObj, setEditingObj } = useMyServiceContext<'eq5d'>();
  const eq5dData = editing?.data ?? viewing?.data ?? {};
  const eq5dLocales = eq5d.localizations;

  const onChangeSurveyForm = (values: TOnChangeValues): void => {
    const field = Object.keys(values)[0];
    if (!field) return;
    const value = Object.values(values)[0];
    if (field === 'date') {
      setEditingData?.({
        ...eq5dData,
        [field]: isPartialDate(value) ? value : eq5dData.date,
      });
    } else {
      setEditingData?.({
        ...eq5dData,
        sections: {
          ...eq5dData.sections,
          [field]: value,
        },
      });
    }
  };

  const fieldOptions = [1, 2, 3, 4, 5];
  const underlineRegex = /<u|\/u>/gm;
  const underlineLocaleSet = eq5dLocales[locale]['yourHealthTodayInfo3'].split(underlineRegex);
  const underlinedLocale = () => {
    return (
      <div>
        {underlineLocaleSet.map(l => {
          const underline = l.includes('>');
          const final = underline ? l.replace('>', '').replace('<', '') : l;
          return (
            <span key={l} style={{ textDecoration: underline ? 'underline' : 'none' }}>
              {final}
            </span>
          );
        })}
      </div>
    );
  };

  const getDescription = (field: keyof NonNullable<IEQ5D['sections']>) => {
    return (
      <div style={{ textTransform: 'uppercase', color: 'black', fontWeight: 600, fontSize: '1.6rem' }}>
        {eq5dLocales[locale][field]}
      </div>
    );
  };

  React.useEffect(() => {
    if (
      editing &&
      !viewing &&
      !eq5dData.date &&
      eq5dData._docCreateDate &&
      partialDateToValue(partialDateFromDate(new Date(eq5dData._docCreateDate))) !==
        partialDateToValue(nowPartialDate())
    ) {
      setEditingData?.({
        ...eq5dData,
        date: partialDateFromDate(new Date(eq5dData._docCreateDate)),
      });
    }
  }, []);

  return (
    <>
      <BlockWrapper
        title={viewing ? formatPartialDate(eq5dData.date) : undefined}
        buttons={
          viewing
            ? [
                {
                  title: 'general.edit',
                  onClick: () => {
                    setViewingObj(null);
                    setEditingObj({ type: 'eq5d', data: eq5dData });
                  },
                },
              ]
            : undefined
        }
      >
        {!viewing && (
          <FormRow title={'general.date'}>
            <InputHandler
              name="date"
              type="PartialDate"
              dateDefault="now"
              formData={{
                document: { date: eq5dData.date },
                onChange: onChangeSurveyForm,
              }}
              editing={!!editing}
            />
          </FormRow>
        )}
        <>
          <FormRow
            title={eq5dLocales[locale]['surveyFillingInfo']}
            description={getDescription('mobility')}
            formatTitle={false}
          >
            <OptionChooser
              formData={{
                document: { mobility: eq5dData.sections?.mobility },
                onChange: onChangeSurveyForm,
              }}
              name="mobility"
              opts={fieldOptions}
              optionFormatter={o => <>{eq5dLocales[locale][`opts.mobility.${o}`]}</>}
              qNumberHidden
              viewing={!!viewing}
            />
          </FormRow>
          <FormRow
            title={eq5dLocales[locale]['surveyFillingInfo']}
            description={getDescription('selfcare')}
            formatTitle={false}
          >
            <OptionChooser
              formData={{
                document: { selfcare: eq5dData.sections?.selfcare },
                onChange: onChangeSurveyForm,
              }}
              name="selfcare"
              opts={fieldOptions}
              optionFormatter={o => <>{eq5dLocales[locale][`opts.selfcare.${o}`]}</>}
              qNumberHidden
              viewing={!!viewing}
            />
          </FormRow>
          <FormRow
            title={eq5dLocales[locale]['surveyFillingInfo']}
            description={getDescription('usualActivities')}
            formatTitle={false}
          >
            <OptionChooser
              formData={{
                document: { usualActivities: eq5dData.sections?.usualActivities },
                onChange: onChangeSurveyForm,
              }}
              name="usualActivities"
              opts={fieldOptions}
              optionFormatter={o => <>{eq5dLocales[locale][`opts.usualActivities.${o}`]}</>}
              qNumberHidden
              viewing={!!viewing}
            />
          </FormRow>
          <FormRow
            title={eq5dLocales[locale]['surveyFillingInfo']}
            description={getDescription('painDiscomfort')}
            formatTitle={false}
          >
            <OptionChooser
              formData={{
                document: { painDiscomfort: eq5dData.sections?.painDiscomfort },
                onChange: onChangeSurveyForm,
              }}
              name="painDiscomfort"
              opts={fieldOptions}
              optionFormatter={o => <>{eq5dLocales[locale][`opts.painDiscomfort.${o}`]}</>}
              qNumberHidden
              viewing={!!viewing}
            />
          </FormRow>
          <FormRow
            title={eq5dLocales[locale]['surveyFillingInfo']}
            description={getDescription('anxietyDepression')}
            formatTitle={false}
          >
            <OptionChooser
              formData={{
                document: { anxietyDepression: eq5dData.sections?.anxietyDepression },
                onChange: onChangeSurveyForm,
              }}
              name="anxietyDepression"
              opts={fieldOptions}
              optionFormatter={o => <>{eq5dLocales[locale][`opts.anxietyDepression.${o}`]}</>}
              qNumberHidden
              viewing={!!viewing}
            />
          </FormRow>
          <FormRow
            title={''}
            formatTitle={false}
            description={
              <div style={{ fontSize: '1.6rem' }}>
                <ul>
                  <li>{eq5dLocales[locale]['yourHealthTodayInfo1']}</li>
                  <li>{eq5dLocales[locale]['yourHealthTodayInfo2']}</li>
                  <li>{underlinedLocale()}</li>
                  <li>{eq5dLocales[locale]['yourHealthTodayInfo4']}</li>
                </ul>
              </div>
            }
          >
            <InputHandler
              type="VerticalVas"
              name="yourHealthToday"
              editing={!!editing}
              infoValueDisplay={eq5dLocales[locale]['yourHealthToday']}
              infoMax={eq5dLocales[locale]['yourHealthTodayInfoTop']}
              infoMin={eq5dLocales[locale]['yourHealthTodayInfoBottom']}
              formData={{
                document: { yourHealthToday: eq5dData.sections?.yourHealthToday },
                onChange: onChangeSurveyForm,
              }}
            />
          </FormRow>
          <FormRow title={'myService.sleepApnea.eq5d.score'}>
            <div style={{ fontWeight: 600 }}>
              <span>{eq5d.calculateScore(eq5dData) ?? '-'}</span>
              <span>{` / ${Task_Progress.calculateProgress('eq5d', omitControlProps(eq5dData)).ceiling}`}</span>
            </div>
          </FormRow>
          <div style={{ width: '100%', textAlign: 'center', fontStyle: 'italic' }}>
            {eq5dLocales[locale]['reference']}
          </div>
        </>
      </BlockWrapper>
    </>
  );
};
